import { axiosJSONInstance } from '@/config';
import {
  AdvanceBatchDetailResponse,
  AdvanceBatchFilters,
  AdvanceBatchListResponse,
  AdvanceBatchOrdersResponse,
  AdvanceCodActivationRequestResponse,
  AdvanceCodSummaryResponse,
  FfcTransfersResponse,
  FFC_LEDGER_SUBJECTS,
  TranslationListResponse,
} from '@/types';

/**
 * Advance COD (BoxyCapital × FFC) — merchant read-only surfaces.
 * Mirrors remittanceService: axiosJSONInstance, page/perpage pagination,
 * house envelope {status, code, message, object}.
 */

// Fetch financing summary (credit limits + program state)
export const fetchAdvanceCodSummary = async (): Promise<AdvanceCodSummaryResponse> => {
  const response = await axiosJSONInstance.get('/merchants/advance-cod/summary');
  return response.data;
};

// Request Advance COD activation (R1). Idempotent server-side: one open
// request per merchant — a repeat POST returns the existing one, so the
// response is always safe to re-render from.
export const requestAdvanceCodActivation =
  async (): Promise<AdvanceCodActivationRequestResponse> => {
    const response = await axiosJSONInstance.post('/merchants/advance-cod/request');
    return response.data;
  };

// Fetch advance batches (server-paginated, filterable)
export const fetchAdvanceBatches = async (
  page: number,
  pageSize?: number,
  filters?: AdvanceBatchFilters,
): Promise<AdvanceBatchListResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    perpage: String(pageSize || 25),
  });
  if (filters?.filter_status) params.append('filter_status', filters.filter_status);
  if (filters?.filter_created_at_from)
    params.append('filter_created_at_from', filters.filter_created_at_from);
  if (filters?.filter_created_at_to)
    params.append('filter_created_at_to', filters.filter_created_at_to);
  if (filters?.filter_platform_code)
    params.append('filter_platform_code', filters.filter_platform_code);

  const response = await axiosJSONInstance.get(
    `/merchants/advance-cod/batches?${params.toString()}`,
  );
  return response.data;
};

// Fetch a single advance batch by UID (detail + orders + settlements)
export const fetchAdvanceBatchByUID = async (uid: string): Promise<AdvanceBatchDetailResponse> => {
  const response = await axiosJSONInstance.get(`/merchants/advance-cod/batches/${uid}`);
  return response.data;
};

// Fetch a batch's financed orders (server-paginated; batches can hold 500 —
// session-12 rework, same page/perpage envelope as every merchant list)
export const fetchAdvanceBatchOrders = async (
  uid: string,
  page: number,
  pageSize?: number,
  excludedOnly?: boolean,
): Promise<AdvanceBatchOrdersResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    perpage: String(pageSize || 25),
  });
  if (excludedOnly) params.append('filter_excluded', '1');

  const response = await axiosJSONInstance.get(
    `/merchants/advance-cod/batches/${uid}/orders?${params.toString()}`,
  );
  return response.data;
};

// Fetch the FFC settlement transfers list ("Transfers to FFC", W6). House
// page/perpage envelope — same shape as every other merchant list surface.
export const fetchAdvanceCodTransfers = async (
  page: number,
  pageSize?: number,
): Promise<FfcTransfersResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    perpage: String(pageSize || 25),
  });
  const response = await axiosJSONInstance.get(
    `/merchants/advance-cod/transfers?${params.toString()}`,
  );
  return response.data;
};

/** Ledger filters (server-side — the feed is /merchants/transactions). */
export interface AdvanceLedgerFilters {
  filter_subject?: string;
  filter_created_at_from?: string;
  filter_created_at_to?: string;
}

// Fetch the FFC mirror-ledger rows: /merchants/transactions server-filtered
// to the ffc_* subjects (remittance-pattern pagination, session-12 rework).
export const fetchAdvanceLedger = async (
  page: number,
  pageSize?: number,
  filters?: AdvanceLedgerFilters,
): Promise<TranslationListResponse> => {
  const params = new URLSearchParams({
    page: String(page),
    perpage: String(pageSize || 25),
  });
  if (filters?.filter_subject) {
    params.append('filter_subjects[]', filters.filter_subject);
  } else {
    FFC_LEDGER_SUBJECTS.forEach((subject) => params.append('filter_subjects[]', subject));
  }
  if (filters?.filter_created_at_from)
    params.append('filter_created_at_from', filters.filter_created_at_from);
  if (filters?.filter_created_at_to)
    params.append('filter_created_at_to', filters.filter_created_at_to);

  const response = await axiosJSONInstance.get(`/merchants/transactions?${params.toString()}`);
  return response.data;
};
